import React, { useEffect } from 'react'
import { ThemeContextValue, uiPrimitives, useThemedStyles } from '../theme'
import { LegalDoc } from './registrationAgreement'

/**
 * 协议全文展示弹窗。
 *
 * 版式与 NoticeModal / AppDialog 同一规范（backdrop / paper2 盒 / serif 文字），
 * 深浅主题自动跟随。与保存确认那类「短确认」不同：协议是长文，需要**可滚动的正文**
 * 和 Esc 关闭。只读展示，不含「同意」动作 —— 同意由注册页那个勾选框表达，
 * 弹窗只是让人能读到全文（法律上"能查阅"是勾选有效的前提）。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	backdrop: {
		position: 'fixed' as const,
		// 不用 `inset:0` 简写：UC/微信等内核（Chromium < 87）不认，逐条写偏移（同 NoticeModal）
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		background: 'rgba(0,0,0,.42)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2000,
		padding: 16,
	},
	box: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		// 协议是长文，正文区自己滚，弹窗本身不超出视口
		display: 'flex',
		flexDirection: 'column' as const,
		width: 620,
		maxWidth: '100%',
		maxHeight: '82vh',
		color: t.palette.ink,
	},
	head: {
		padding: '20px 24px 14px',
		borderBottom: `1px solid ${t.palette.line}`,
		flex: 'none',
	},
	title: {
		fontFamily: t.fonts.serif,
		fontSize: 18,
		fontWeight: 700,
		margin: '0 0 4px',
	},
	meta: {
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
	},
	body: {
		padding: '18px 24px',
		overflowY: 'auto' as const,
		fontFamily: t.fonts.serif,
		fontSize: 13.5,
		lineHeight: 1.85,
		color: t.palette.ink2,
	},
	intro: {
		margin: '0 0 16px',
		whiteSpace: 'pre-wrap' as const,
		wordBreak: 'break-word',
	},
	section: {
		marginBottom: 14,
	},
	sectionHead: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		fontWeight: 600,
		color: t.palette.ink,
		marginBottom: 5,
	},
	item: {
		margin: '0 0 6px',
		whiteSpace: 'pre-wrap' as const,
		wordBreak: 'break-word',
	},
	closing: {
		margin: '16px 0 0',
		paddingTop: 14,
		borderTop: `1px solid ${t.palette.line}`,
		fontWeight: 600,
		color: t.palette.ink,
	},
	acts: {
		padding: '14px 24px 18px',
		borderTop: `1px solid ${t.palette.line}`,
		display: 'flex',
		justifyContent: 'flex-end',
		flex: 'none',
	},
})

export function LegalDocModal(props: {doc: LegalDoc, onClose: () => void}) {
	const classes = useThemedStyles(stylesFactory)
	const {doc} = props

	// Esc 关闭。按 document 监听而不是给 box 加 onKeyDown —— 弹窗打开时焦点还在
	// 触发它的按钮上，键盘事件不会自然走到这个 div（同 NoticeModal 的处理）。
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') props.onClose()
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [props.onClose])

	return (
		<div className={classes.backdrop} onClick={props.onClose}>
			<div className={classes.box} role="dialog" aria-modal="true" aria-label={doc.title}
				onClick={e => e.stopPropagation()}>
				<div className={classes.head}>
					<h3 className={classes.title}>{doc.title}</h3>
					<div className={classes.meta}>版本 {doc.version} · 生效日期：{doc.effectiveDate}</div>
				</div>
				<div className={classes.body}>
					{doc.intro.map((p, i) => <p key={i} className={classes.intro}>{p}</p>)}
					{doc.sections.map((sec, i) => (
						<div key={i} className={classes.section}>
							<div className={classes.sectionHead}>{sec.heading}</div>
							{sec.items.map((it, j) => <p key={j} className={classes.item}>{it}</p>)}
						</div>
					))}
					<p className={classes.closing}>{doc.closing}</p>
				</div>
				<div className={classes.acts}>
					<button className={`${classes.tb} ${classes.tbPrimary}`} onClick={props.onClose}>我已阅读</button>
				</div>
			</div>
		</div>
	)
}
